const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

export const persistenceValueEqual = (left, right) => {
  if (Object.is(left, right)) return true;
  if (left === null || right === null || typeof left !== 'object' || typeof right !== 'object') {
    return false;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    return left.every((value, index) => persistenceValueEqual(value, right[index]));
  }
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key, index) => (
    key === rightKeys[index] && persistenceValueEqual(left[key], right[key])
  ));
};

const mergeValue = (base, local, remote) => {
  if (persistenceValueEqual(local, base)) return remote;
  if (persistenceValueEqual(remote, base)) return local;
  if (persistenceValueEqual(local, remote)) return local;

  const canMergeObjects = local && remote
    && typeof local === 'object'
    && typeof remote === 'object'
    && !Array.isArray(local)
    && !Array.isArray(remote);
  if (!canMergeObjects) return local;

  const output = {};
  const keys = new Set([
    ...Object.keys(base && typeof base === 'object' && !Array.isArray(base) ? base : {}),
    ...Object.keys(remote),
    ...Object.keys(local)
  ]);
  keys.forEach((key) => {
    const baseValue = hasOwn(base, key) ? base[key] : undefined;
    const localValue = hasOwn(local, key) ? local[key] : undefined;
    const remoteValue = hasOwn(remote, key) ? remote[key] : undefined;
    const merged = mergeValue(baseValue, localValue, remoteValue);
    if (merged !== undefined) output[key] = merged;
  });
  return output;
};

const indexById = (items) => new Map(
  (Array.isArray(items) ? items : [])
    .filter((item) => item && item.id !== undefined && item.id !== null)
    .map((item) => [String(item.id), item])
);

export const mergePersistenceCollection = (baseItems, localItems, remoteItems) => {
  const base = indexById(baseItems);
  const local = indexById(localItems);
  const remote = indexById(remoteItems);
  const orderedIds = [];
  const seen = new Set();

  [remoteItems, localItems, baseItems].forEach((items) => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (!item || item.id === undefined || item.id === null) return;
      const id = String(item.id);
      if (!seen.has(id)) {
        seen.add(id);
        orderedIds.push(id);
      }
    });
  });

  return orderedIds.reduce((result, id) => {
    const merged = mergeValue(base.get(id), local.get(id), remote.get(id));
    if (merged !== undefined) result.push(merged);
    return result;
  }, []);
};

export const mergeTimelinePersistenceSnapshots = (base = {}, local = {}, remote = {}) => ({
  timelineClips: mergePersistenceCollection(
    base.timelineClips,
    local.timelineClips,
    remote.timelineClips
  ),
  tracks: mergePersistenceCollection(base.tracks, local.tracks, remote.tracks),
  buses: mergePersistenceCollection(base.buses, local.buses, remote.buses),
  automation: mergeValue(base.automation || {}, local.automation || {}, remote.automation || {}) || {}
});

export const assessTimelineSaveSafety = ({
  preventEmptyOverwrite = false,
  allowClipClear = false,
  allowAudioPrune = false,
  sanitizedClipCount = 0,
  sanitizedAudioClipCount = 0,
  audioClipCount = 0,
  trackCount = 0,
  previousClipCount = 0
} = {}) => {
  if (preventEmptyOverwrite && sanitizedClipCount === 0 && trackCount === 0) {
    return { safe: false, reason: 'prevent_empty_timeline_overwrite' };
  }
  if (
    preventEmptyOverwrite
    && allowClipClear !== true
    && sanitizedClipCount === 0
    && previousClipCount > 0
  ) {
    return { safe: false, reason: 'prevent_empty_clip_overwrite' };
  }
  if (!allowAudioPrune && audioClipCount > 0 && trackCount === 0) {
    return { safe: false, reason: 'prevent_incomplete_track_metadata' };
  }
  if (!allowAudioPrune && sanitizedAudioClipCount < audioClipCount) {
    return { safe: false, reason: 'prevent_audio_clip_prune' };
  }
  return { safe: true, reason: null };
};
